/**
 * Real customer auth (Phase 2) + Phase 8.2 geo-shield probe.
 *
 * Backed by /api/saas/auth/{register,login,me} with JWT in localStorage.
 * The token is also attached on every request via lib/api.js — this context
 * just stores the rich `user` object (customer + bouquets + lines) and
 * triggers re-fetches when the underlying state changes.
 *
 * Phase 8.2: On mount we also probe /saas/config/geo (which is exempt from
 * the geo middleware) — if `enforced && !allowed`, the Provider renders a
 * fullscreen <GeoBlocked /> overlay instead of children. This way EVERY
 * customer-facing route is protected without per-page guards. Admin routes
 * are completely unaffected because they live outside this provider's UI.
 */
import { createContext, useContext, useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import GeoBlocked from "@/saas/pages/GeoBlocked";

const Ctx = createContext(null);
const TOKEN_KEY = "saas.token";

function resolveApiError(err, fallback) {
  const detail = err?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    const first = detail[0];
    if (typeof first === "string") return first;
    if (first?.msg) return first.msg;
  }
  if (detail?.msg) return detail.msg;
  return fallback;
}

export function SaasAuthProvider({ children }) {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [user, setUser]   = useState(null);
  const [loading, setLoading] = useState(true);

  // Phase 8.2 — Geo-shield probe state
  const [geoState, setGeoState] = useState(null); // null | {enforced, allowed, detected_country, allowed_countries}

  const persist = (t) => {
    if (t) localStorage.setItem(TOKEN_KEY, t);
    else   localStorage.removeItem(TOKEN_KEY);
    setToken(t);
  };

  // Smart-Link auto-auth: token query param ile sessiz giriş.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const qs = new URLSearchParams(window.location.search);
    const linkToken = qs.get("token");
    if (!linkToken || linkToken === token) return;
    persist(linkToken);
    qs.delete("token");
    const next = `${window.location.pathname}${qs.toString() ? `?${qs.toString()}` : ""}${window.location.hash || ""}`;
    window.history.replaceState({}, "", next);
  }, [token]);

  const fetchMe = useCallback(async () => {
    if (!token) { setUser(null); setLoading(false); return; }
    try {
      const res = await api.get("/saas/auth/me", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUser(res.data);
    } catch (err) {
      if (err?.response?.status === 401) persist(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchMe(); }, [fetchMe]);

  // Geo probe — runs once on mount; refreshes if window regains focus after
  // a long idle (helps when admin toggles config while the customer tab is open).
  useEffect(() => {
    let alive = true;
    const probe = async () => {
      try {
        const res = await api.get("/saas/config/geo");
        if (alive) setGeoState(res.data);
      } catch {
        // Silent — probe failures should not block the app.
        if (alive) setGeoState({ enforced: false, allowed: true });
      }
    };
    probe();
    const onFocus = () => { probe(); };
    window.addEventListener("focus", onFocus);
    return () => { alive = false; window.removeEventListener("focus", onFocus); };
  }, []);

  const login = async ({ email, password }) => {
    try {
      const res = await api.post("/saas/auth/login", { email, password });
      persist(res.data.access_token);
      return { ok: true };
    } catch (err) {
      return { ok: false, error: err?.response?.data?.detail || "Giriş başarısız." };
    }
  };

  const register = async ({ full_name, email, password, terms_accepted, terms_version }) => {
    try {
      const res = await api.post("/saas/auth/register", {
        full_name, email, password,
        // Phase 9.1 — Optional legal acceptance forwarded to backend.
        ...(terms_accepted ? { terms_accepted: true, terms_version } : {}),
      });
      persist(res.data.access_token);
      return { ok: true };
    } catch (err) {
      return { ok: false, error: resolveApiError(err, "Kayıt başarısız.") };
    }
  };

  const logout = () => { persist(null); setUser(null); };

  const authed = useCallback((config = {}) => ({
    ...config,
    headers: { ...(config.headers || {}), Authorization: `Bearer ${token}` },
  }), [token]);

  // Geo-block overlay short-circuits the app for blocked regions.
  // Admin routes are explicitly exempt — operators must remain reachable
  // even from outside the allowed list (their own auth gate still applies).
  const isAdminPath =
    typeof window !== "undefined" &&
    window.location.pathname.startsWith("/admin");
  if (
    !isAdminPath &&
    geoState &&
    geoState.enforced &&
    geoState.allowed === false
  ) {
    return (
      <GeoBlocked
        country={geoState.detected_country}
        allowed_countries={geoState.allowed_countries || []}
      />
    );
  }

  return (
    <Ctx.Provider value={{ user, token, loading, login, register, logout, refresh: fetchMe, authed }}>
      {children}
    </Ctx.Provider>
  );
}

export function useSaasAuth() { return useContext(Ctx); }
